import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import CustomButton from "global/components/CustomButton/CustomButton";
import viewpaths from "global/constants/ViewPathConstants";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import homeStyles from "../Home.styles";

// Shared blog topics data
const blogTopics = [
  {
    key: "hydroponics",
    label: "Hydroponics",
    description: "Explore sustainable farming and innovative growing techniques",
    image: "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=200&h=200&fit=crop",
  },
  {
    key: "technology",
    label: "Technology",
    description: "Latest tech trends and digital transformation",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=200&h=200&fit=crop",
  },
  {
    key: "innovation",
    label: "Innovation",
    description: "Breakthrough ideas and cutting-edge solutions",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=200&h=200&fit=crop",
  },
  {
    key: "wellness",
    label: "Wellness",
    description: "Health, fitness, and holistic well-being",
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=200&h=200&fit=crop",
  },
  {
    key: "finance",
    label: "Finance",
    description: "Financial strategies and market insights",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop",
  },
  {
    key: "philanthropy",
    label: "Philanthropy",
    description: "Giving back and making positive impact",
    image: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=200&h=200&fit=crop",
  },
  {
    key: "entrepreneurship",
    label: "Entrepreneurship",
    description: "Startup stories and business strategies",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=200&h=200&fit=crop",
  },
  // {
  //   key: "social-entrepreneurship",
  //   label: "Social Entrepreneurship",
  //   description: "Business for good and sustainable change",
  //   image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=200&h=200&fit=crop",
  // },
];

// Compact Card component (Layout 1 - Grid)
const CompactCard = ({
  topic,
  index,
  onHover,
  isHovered,
  muiTheme,
  history,
  classes,
}: any) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 500, damping: 100 });
  const mouseYSpring = useSpring(y, { stiffness: 500, damping: 100 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["5deg", "-5deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-5deg", "5deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const xPct = (e.clientX - rect.left) / rect.width - 0.5;
    const yPct = (e.clientY - rect.top) / rect.height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => onHover(index)}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      whileHover={{ scale: 1.03, y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <Box onClick={() => history.push(`${viewpaths.blogsViewPath}?topic=${topic.key}`)} sx={classes.compactCard(muiTheme)}>
        <Box sx={classes.compactCardImageContainer(muiTheme)}>
          <motion.div animate={{ scale: isHovered ? 1.08 : 1 }} transition={{ duration: 0.4 }}>
            <Box component="img" src={topic.image} alt={topic.label} sx={classes.compactCardImage(isHovered)} />
          </motion.div>
        </Box>

        <Box sx={classes.compactCardContent}>
          <Stack direction="column" spacing={1.5}>
            <Typography sx={classes.compactCardTitle(muiTheme, isHovered)}>
              {topic.label}
            </Typography>
            <Typography sx={{ ...classes.subHeaderText, ...classes.compactCardDescription }}>
              {topic.description}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, opacity: isHovered ? 1 : 0.7 }}>
              <Typography sx={classes.compactCardExploreText(muiTheme, isHovered)}>
                Explore
              </Typography>
              <motion.div animate={{ x: isHovered ? 6 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                <EastOutlinedIcon sx={classes.compactCardExploreIcon(isHovered)} />
              </motion.div>
            </Stack>
          </Stack>
        </Box>

        <motion.div
          style={{
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            ...classes.compactCardShineEffect(muiTheme),
          }}
          animate={{ left: isHovered ? "100%" : "-100%" }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        />
        
        {/* Orange Accent Corner */}
        {isHovered && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "60px",
              height: "60px",
              background: `linear-gradient(135deg, ${alpha("#f8481c", 0.4)} 0%, transparent 100%)`,
              borderRadius: "0 4px 0 0",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    </motion.div>
  );
};

// Carousel Card component (Layout 2 - Carousel) - FIXED
const CarouselCard = ({ topic, index, isActive, isCenterCard, isSideCard, muiTheme, history, classes }: any) => {
  // Determine opacity and scale based on card position
  const getCardStyle = () => {
    if (isCenterCard) {
      return { opacity: 1, scale: 1 };
    } else if (isSideCard) {
      return { opacity: 0.5, scale: 0.95 };
    } else {
      return { opacity: isActive ? 1 : 0.7, scale: isActive ? 1 : 0.97 };
    }
  };

  const cardStyle = getCardStyle();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: cardStyle.opacity, scale: cardStyle.scale }}
      transition={{ duration: 0.4 }}
      style={{ 
        width: "100%", 
        height: "100%",
        flexShrink: 0,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box onClick={() => history.push(`${viewpaths.blogsViewPath}?topic=${topic.key}`)} sx={classes.carouselCard(muiTheme)}>
        {/* Image Section */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "55%",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={topic.image}
            alt={topic.label}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: isActive ? "scale(1.05)" : "scale(1)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "40%",
              background: `linear-gradient(to top, ${alpha("#f8481c", isActive ? 0.4 : 0.2)} 0%, rgba(0,0,0,${isActive ? 0.5 : 0.4}) 30%, transparent 100%)`,
              transition: "all 0.3s ease",
            }}
          />
          {/* Orange glow on active card */}
          {isActive && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "100%",
                background: `radial-gradient(ellipse at center bottom, ${alpha("#f8481c", 0.2)} 0%, transparent 70%)`,
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: { xs: 3, md: 4 },
            background: muiTheme.palette.background.paper,
            borderTop: `2px solid ${alpha("#f8481c", isActive ? 0.4 : 0.1)}`,
            transition: "all 0.3s ease",
          }}
        >
          <Stack direction="column" spacing={1.5}>
            <Typography sx={classes.carouselCardTitle(muiTheme, isActive)}>
              {topic.label}
            </Typography>
            <Typography sx={classes.carouselCardDescription}>
              {topic.description}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={classes.carouselCardExploreText(muiTheme)}>
                Explore
              </Typography>
              <EastOutlinedIcon sx={classes.carouselCardExploreIcon} />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </motion.div>
  );
};


const OurFeaturesSection = ({
  section2Ref,
  layoutType = "grid", // "grid" | "carousel"
}: {
  section2Ref: React.RefObject<HTMLElement>;
  layoutType?: "grid" | "carousel";
}) => {
  const history = useHistory();
  const muiTheme = useTheme();
  const classes = homeStyles;
  const { t } = useTranslation();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [, forceUpdate] = useState({});
  const slideCounterRef = useRef(0); // Always increments, never decrements (except manual prev)
  const baseOffsetRef = useRef(0); // Tracks total distance scrolled (never decreases)

  // Carousel navigation - TRUE INFINITE SCROLL (one direction)
  const itemsPerView = mdUp ? 4 : 1;
  // Duplicate topics multiple times for seamless infinite scroll
  const duplicatedTopics = [...blogTopics, ...blogTopics, ...blogTopics];
  const originalLength = blogTopics.length;
  
  const triggerUpdate = () => forceUpdate({});
  
  const nextSlide = () => {
    slideCounterRef.current += 1;
    baseOffsetRef.current += 1;
    
    // When counter wraps, reset both to maintain seamless loop
    // Reset happens when we complete one full cycle
    if (slideCounterRef.current >= originalLength) {
      slideCounterRef.current = slideCounterRef.current % originalLength;
      // Reset baseOffset to match counter position to keep translateX bounded
      baseOffsetRef.current = slideCounterRef.current;
    }
    
    triggerUpdate();
  };

  const prevSlide = () => {
    if (slideCounterRef.current > 0) {
      slideCounterRef.current -= 1;
      baseOffsetRef.current = Math.max(0, baseOffsetRef.current - 1);
    }
    triggerUpdate();
  };

  useEffect(() => {
    const updateCarouselWidth = () => {
      if (carouselContainerRef.current) {
        setCarouselWidth(carouselContainerRef.current.offsetWidth);
      }
    };

    updateCarouselWidth();
    window.addEventListener("resize", updateCarouselWidth);
    return () => window.removeEventListener("resize", updateCarouselWidth);
  }, [mdUp]);

  // Auto-scroll functionality - Continuous forward scroll (never goes backward)
  useEffect(() => {
    if (layoutType !== "carousel" || isPaused) return;

    const interval = setInterval(() => {
      slideCounterRef.current += 1;
      baseOffsetRef.current += 1;
      
      // When counter wraps, reset both to maintain seamless loop
      // Reset happens when we complete one full cycle
      if (slideCounterRef.current >= originalLength) {
        slideCounterRef.current = slideCounterRef.current % originalLength;
        // Reset baseOffset to match counter position to keep translateX bounded
        baseOffsetRef.current = slideCounterRef.current;
      }
      
      // Force re-render to update visual position
      triggerUpdate();
    }, 3000); // Auto-advance every 3 seconds

    return () => clearInterval(interval);
  }, [layoutType, isPaused, originalLength]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 100, scale: 0.85, rotateX: -20 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };


  const renderLayout = () => {
    switch (layoutType) {
      case "carousel":
        const gap = 12;
        
        // Calculate exact pixel offset based on counter (always forward)
        const cardWidth = carouselWidth > 0 
          ? (carouselWidth - (gap * (itemsPerView - 1))) / itemsPerView 
          : 0;
        const totalWidth = cardWidth + gap;
        
        // Map counter to visual position in second set for seamless loop
        const positionInSet = slideCounterRef.current % originalLength;
        const visualSlideOffset = originalLength + positionInSet;
        
        // Calculate translateX from baseOffset - bounded to prevent cards disappearing
        // baseOffset resets when counter wraps, keeping translateX within visible range
        // We add originalLength offset to show items from second set
        const translateXPixels = (baseOffsetRef.current + originalLength) * totalWidth;
        
        return (
          <motion.div
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.34, 1.56, 0.64, 1] }}
          >
          <Box 
            sx={{ position: "relative", width: "100%" }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Carousel Container with overflow hidden */}
            <Box
              ref={carouselContainerRef}
              sx={{
                position: "relative",
                overflow: "hidden",
                width: "100%",
                mb: 4,
                borderRadius: "8px",
              }}
            >
              {/* Sliding Track */}
              <motion.div
                ref={carouselTrackRef}
                animate={{
                  x: carouselWidth > 0 ? `-${translateXPixels}px` : `-${visualSlideOffset * (100 / itemsPerView)}%`,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                style={{
                  display: "flex",
                  gap: `${gap}px`,
                  width: "100%",
                }}
              >
                {duplicatedTopics.map((topic, index) => {
                  // Calculate relative position for center/side card detection
                  const positionInSet = slideCounterRef.current % originalLength;
                  const visualSlideOffset = originalLength + positionInSet;
                  const relativePosition = mdUp ? index - visualSlideOffset : 0;
                  const isCenterCard = mdUp && itemsPerView === 4 && (relativePosition === 1 || relativePosition === 2);
                  const isSideCard = mdUp && itemsPerView === 4 && (relativePosition === 0 || relativePosition === 3);
                  const isVisible = mdUp 
                    ? index >= visualSlideOffset && index < visualSlideOffset + itemsPerView
                    : index === visualSlideOffset;
                  
                  return (
                    <Box
                      key={`${topic.key}-${index}`}
                      sx={{
                        width: mdUp ? `calc((100% - ${gap * (itemsPerView - 1)}px) / ${itemsPerView})` : "100%",
                        flexShrink: 0,
                        py: 1.5
                      }}
                    >
                      <CarouselCard
                        topic={topic}
                        index={index}
                        isActive={isVisible}
                        isCenterCard={isCenterCard}
                        isSideCard={isSideCard}
                        muiTheme={muiTheme}
                        history={history}
                        classes={classes}
                      />
                    </Box>
                  );
                })}
              </motion.div>
            </Box>
            
            {/* Navigation Controls */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Stack direction="row" justifyContent="center" spacing={2} alignItems="center">
                <motion.div
                  initial={{ opacity: 0, x: -30, rotate: -90 }}
                  whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5, ease: [0.68, -0.55, 0.265, 1.55] }}
                  whileHover={{ scale: 1.1, rotate: -5 }}
                >
                  <IconButton
                    onClick={prevSlide}
                    sx={{
                      background: alpha(muiTheme.palette.background.paper, 0.5),
                      backdropFilter: "blur(20px)",
                      border: `1px solid ${alpha("#f8481c", 0.3)}`,
                      "&:hover": {
                        background: alpha("#f8481c", 0.1),
                        border: `1px solid ${alpha("#f8481c", 0.6)}`,
                        transform: "scale(1.1)",
                        boxShadow: `0 0 20px ${alpha("#f8481c", 0.4)}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: "18px", color: "#f8481c" }} />
                  </IconButton>
                </motion.div>
                
                {/* Dot Indicators - Show only for original topics */}
                <Stack direction="row" spacing={1} alignItems="center">
                  {Array.from({ length: originalLength }).map((_, index) => {
                    // Map current slide to original index for dot highlighting
                    const positionInSet = slideCounterRef.current % originalLength;
                    const mappedIndex = positionInSet;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0, rotate: 180 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ 
                          duration: 0.5, 
                          delay: 0.6 + index * 0.1,
                          ease: [0.68, -0.55, 0.265, 1.55]
                        }}
                        whileHover={{ scale: 1.3 }}
                      >
                        <Box
                          onClick={() => {
                            slideCounterRef.current = originalLength + index;
                            triggerUpdate();
                          }}
                          sx={classes.carouselDotIndicator(muiTheme, index === mappedIndex)}
                        />
                      </motion.div>
                    );
                  })}
                </Stack>
                
                <motion.div
                  initial={{ opacity: 0, x: 30, rotate: 90 }}
                  whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5, ease: [0.68, -0.55, 0.265, 1.55] }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <IconButton
                    onClick={nextSlide}
                    sx={{
                      background: alpha(muiTheme.palette.background.paper, 0.5),
                      backdropFilter: "blur(20px)",
                      border: `1px solid ${alpha("#f8481c", 0.3)}`,
                      "&:hover": {
                        background: alpha("#f8481c", 0.1),
                        border: `1px solid ${alpha("#f8481c", 0.6)}`,
                        transform: "scale(1.1)",
                        boxShadow: `0 0 20px ${alpha("#f8481c", 0.4)}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: "18px", color: "#f8481c" }} />
                  </IconButton>
                </motion.div>
              </Stack>
            </motion.div>
          </Box>
          </motion.div>
        );


      default: // "grid"
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            style={{
              display: "grid",
              gridTemplateColumns: mdUp ? "repeat(4, 1fr)" : "repeat(2, 1fr)",
              gap: mdUp ? "20px" : "16px",
            }}
          >
            {blogTopics.map((topic, index) => (
              <motion.div 
                key={topic.key} 
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03, 
                  y: -8,
                  transition: { duration: 0.3 }
                }}
              >
                <CompactCard
                  topic={topic}
                  index={index}
                  onHover={setHoveredIndex}
                  isHovered={hoveredIndex === index}
                  muiTheme={muiTheme}
                  history={history}
                  classes={classes}
                />
              </motion.div>
            ))}
          </motion.div>
        );
    }
  };

  return (
    <React.Fragment>
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
        `}
      </style>
      <Box
        ref={section2Ref}
        py={{ md: 12, xs: 8 }}
        sx={classes.featuresSectionContainer(muiTheme)}
      >
      {/* Static Creative Background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {/* Static Gradient Mesh */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <Box sx={classes.featuresGradientMesh(muiTheme)} />
        </motion.div>

        {/* Static Grid Pattern */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: muiTheme.palette.mode === "dark" ? 0.5 : 0.5 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                muiTheme.palette.mode === "dark"
                  ? `linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px)`
                  : `linear-gradient(rgba(0, 0, 0, 0.06) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(0, 0, 0, 0.06) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          />
        </motion.div>

        {/* Static Gradient Orbs */}
        {[0, 1, 2, 3].map((index) => (
          <motion.div
            key={`orb-${index}`}
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ 
              duration: 1, 
              delay: 0.5 + index * 0.2,
              ease: [0.68, -0.55, 0.265, 1.55]
            }}
          >
            <Box sx={classes.featuresGradientOrb(muiTheme, index)} />
          </motion.div>
        ))}

        {/* Static Geometric Shapes */}
        {[0, 1, 2, 3].map((index) => {
          const shapes = ["circle", "polygon"];
          const shapeType = shapes[index % shapes.length];
          return (
            <motion.div
              key={`shape-${index}`}
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              whileInView={{ opacity: muiTheme.palette.mode === "dark" ? 0.3 : 0.3, scale: 1, rotate: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ 
                duration: 1.2, 
                delay: 0.7 + index * 0.15,
                ease: [0.68, -0.55, 0.265, 1.55]
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: `${15 + index * 20}%`,
                  top: `${15 + index * 20}%`,
                  width: `${150 + index * 80}px`,
                  height: `${150 + index * 80}px`,
                  borderRadius: shapeType === "circle" ? "50%" : "12%",
                  clipPath:
                    shapeType === "polygon"
                      ? index % 2 === 0
                        ? "polygon(50% 0%, 0% 100%, 100% 100%)"
                        : "polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)"
                      : "none",
                  background:
                    index === 1
                      ? muiTheme.palette.mode === "dark"
                        ? `linear-gradient(135deg, rgba(248, 72, 28, 0.15) 0%, rgba(248, 72, 28, 0.1) 50%, rgba(248, 72, 28, 0.06) 100%)`
                        : `linear-gradient(135deg, rgba(248, 72, 28, 0.12) 0%, rgba(248, 72, 28, 0.07) 50%, rgba(248, 72, 28, 0.05) 100%)`
                      : muiTheme.palette.mode === "dark"
                      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(220, 220, 220, 0.12) 50%, rgba(180, 180, 180, 0.08) 100%)`
                      : `linear-gradient(135deg, rgba(0, 0, 0, 0.12) 0%, rgba(50, 50, 50, 0.07) 50%, rgba(100, 100, 100, 0.05) 100%)`,
                  filter: "blur(30px)",
                  border: `1px solid ${alpha(index === 1 ? "#f8481c" : muiTheme.palette.divider, index === 1 ? (muiTheme.palette.mode === "dark" ? 0.2 : 0.18) : (muiTheme.palette.mode === "dark" ? 0.15 : 0.15))}`,
                }}
              />
            </motion.div>
          );
        })}
      </Box>

      {/* Dark Overlay for Dark Theme */}
      {muiTheme.palette.mode === "dark" && <Box sx={classes.featuresDarkOverlay} />}

      <Box ref={containerRef} sx={classes.featuresContentContainer}>
        <Container maxWidth="xl">
          <Stack
            direction={"column"}
            spacing={3}
            textAlign={"center"}
            alignItems={"center"}
            mb={{ md: 8, xs: 6 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.9, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0)" }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Typography
                sx={{
                  ...classes.contentHeaderText,
                  color: muiTheme.palette.text.primary,
                  maxWidth: "800px",
                  fontSize: { xs: "28px", md: "40px" },
                  lineHeight: 1.2,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  background:
                    muiTheme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 65%, #f8481c 85%, #ff6b4a 100%)"
                      : "linear-gradient(135deg, #000000 0%, #000000 65%, #f8481c 85%, #ff6b4a 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundSize: "300% 300%",
                  animation: "gradientShift 5s ease infinite",
                }}
              >
                Explore Our Diverse Range of Topics
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50, filter: "blur(8px)" }}
              whileInView={{ opacity: 1, x: 0, filter: "blur(0)" }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Typography
                sx={{
                  ...classes.subHeaderText,
                  color: muiTheme.palette.text.secondary,
                  maxWidth: "600px",
                  fontSize: { xs: "15px", md: "17px" },
                  lineHeight: 1.6,
                }}
              >
                Discover insightful articles covering innovation, wellness, finance,
                and more across various domains
              </Typography>
              {/* Orange accent line under description */}
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                whileInView={{ width: "100%", opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <Box sx={classes.featuresOrangeAccentLine} />
              </motion.div>
            </motion.div>
          </Stack>

          {renderLayout()}

          {/* CTA Section */}
          <Box mt={{ md: 10, xs: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Stack direction={"column"} spacing={3} alignItems={"center"} sx={classes.featuresCTASection(muiTheme)}>
                <motion.div
                  initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                  whileInView={{ opacity: 1, y: 0, filter: "blur(0)" }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  <Typography sx={{ ...classes.contentHeaderText, ...classes.featuresCTATitle(muiTheme) }}>
                    Ready to Explore More?
                  </Typography>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  <Typography sx={{ ...classes.subHeaderText, ...classes.featuresCTADescription }}>
                    Discover in-depth articles, insights, and stories across all our blog topics
                  </Typography>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: 0.6, ease: [0.68, -0.55, 0.265, 1.55] }}
                  whileHover={{ scale: 1.05, y: -4 }}
                >
                  <CustomButton
                    label="View All Blogs"
                    buttonType="gradientBtn"
                    onClick={() => history.push(viewpaths.blogsViewPath)}
                    customClasses={classes.ourServiceBtn}
                    endIcon={<EastOutlinedIcon />}
                  />
                </motion.div>
              </Stack>
            </motion.div>
          </Box>
        </Container>
      </Box>
      </Box>
    </React.Fragment>
  );
};

export default OurFeaturesSection;
